import { useState, useEffect } from "react";
import { webhookApi, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Loader2, Plus, Trash2, Eye, EyeOff, Webhook, Copy, Check, RotateCw } from "lucide-react";
import { PageHeader, SectionCard } from "../components/ui/page-shell";
import { toast } from "sonner";

const WEBHOOK_EVENTS = [
  { id: "new_message", label: "New Message", desc: "When a visitor sends a message" },
  { id: "appointment_booked", label: "Appointment Booked", desc: "When an appointment is created" },
  { id: "lead_captured", label: "Lead Captured", desc: "When visitor info is collected" },
  { id: "session_ended", label: "Session Ended", desc: "When a chat session ends" }
];

export default function Webhooks() {
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newWebhook, setNewWebhook] = useState({ url: "", events: [], redact_pii: true });
  const [secrets, setSecrets] = useState({});
  const [showSecret, setShowSecret] = useState({});
  const [copied, setCopied] = useState({});
  const [showDeliveries, setShowDeliveries] = useState({});
  const [deliveries, setDeliveries] = useState({});
  const [loadingDeliveries, setLoadingDeliveries] = useState({});
  const [retrying, setRetrying] = useState({});

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      const res = await webhookApi.list();
      setWebhooks(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newWebhook.url.trim()) {
      toast.error("Please enter a URL");
      return;
    }
    if (!newWebhook.url.trim().startsWith("https://")) {
      toast.error("Webhook URL must start with https://");
      return;
    }
    if (newWebhook.events.length === 0) {
      toast.error("Select at least one event");
      return;
    }
    setCreating(true);
    try {
      const res = await webhookApi.create(newWebhook);
      setWebhooks([res.data, ...webhooks]);
      setCreateOpen(false);
      setNewWebhook({ url: "", events: [], redact_pii: true });
      toast.success("Webhook created");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id, isActive) => {
    try {
      await webhookApi.update(id, { is_active: !isActive });
      setWebhooks(webhooks.map(w => w.id === id ? { ...w, is_active: !isActive } : w));
      toast.success(isActive ? "Webhook disabled" : "Webhook enabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleToggleRedact = async (id, redactPii) => {
    try {
      await webhookApi.update(id, { redact_pii: !redactPii });
      setWebhooks(webhooks.map(w => w.id === id ? { ...w, redact_pii: !redactPii } : w));
      toast.success(!redactPii ? "PII redaction enabled" : "PII redaction disabled");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this webhook?")) return;
    try {
      await webhookApi.delete(id);
      setWebhooks(webhooks.filter(w => w.id !== id));
      toast.success("Webhook deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const handleRotateSecret = async (id) => {
    if (!window.confirm("Rotate this webhook's signing secret? The current secret will stop working immediately.")) return;
    try {
      const res = await webhookApi.rotateSecret(id);
      setSecrets((prev) => ({ ...prev, [id]: res.data.secret }));
      setShowSecret((prev) => ({ ...prev, [id]: true }));
      toast.success("Secret rotated. Update your endpoint with the new value.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || "Failed to rotate secret");
    }
  };

  const handleShowSecret = async (id) => {
    if (showSecret[id]) {
      setShowSecret({ ...showSecret, [id]: false });
      return;
    }
    if (!secrets[id]) {
      try {
        const res = await webhookApi.getSecret(id);
        setSecrets({ ...secrets, [id]: res.data.secret });
      } catch (e) {
        toast.error("Failed to load secret");
        return;
      }
    }
    setShowSecret({ ...showSecret, [id]: true });
  };

  const copySecret = (id) => {
    try {
      const textarea = document.createElement("textarea");
      textarea.value = secrets[id];
      textarea.style.position = "fixed";
      textarea.style.opacity = "0";
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand("copy");
      document.body.removeChild(textarea);
      setCopied({ ...copied, [id]: true });
      setTimeout(() => setCopied({ ...copied, [id]: false }), 2000);
      toast.success("Secret copied");
    } catch {
      toast.error("Failed to copy. Please select and copy manually.");
    }
  };

const loadDeliveries = async (id) => {
    setLoadingDeliveries(prev => ({ ...prev, [id]: true }));
    try {
      const res = await webhookApi.listDeliveries(id, 20);
      setDeliveries(prev => ({ ...prev, [id]: res?.data?.items || [] }));
    } catch (e) {
      toast.error("Failed to load deliveries");
    } finally {
      setLoadingDeliveries(prev => ({ ...prev, [id]: false }));
    }
  };

  const toggleDeliveries = async (id) => {
    const next = !showDeliveries[id];
    setShowDeliveries(prev => ({ ...prev, [id]: next }));
    if (next && !deliveries[id]) {
      await loadDeliveries(id);
    }
  };

  const handleRetry = async (deliveryId, webhookId) => {
    setRetrying(prev => ({ ...prev, [deliveryId]: true }));
    try {
      const res = await webhookApi.retryDelivery(deliveryId);
      if (res?.data?.ok) {
        toast.success("Delivery retried successfully");
      } else {
        toast.error("Retry failed; check the URL and response");
      }
      await loadDeliveries(webhookId);
    } catch (e) {
      if (e?.response?.status === 410) {
        toast.error("This delivery's data was removed under the 30-day privacy retention policy, so it cannot be resent.");
      } else {
        toast.error(e?.response?.data?.detail || "Retry request failed");
      }
    } finally {
      setRetrying(prev => ({ ...prev, [deliveryId]: false }));
    }
  };

    const toggleEvent = (event) => {
    setNewWebhook(prev => ({
      ...prev,
      events: prev.events.includes(event)
        ? prev.events.filter(e => e !== event)
        : [...prev.events, event]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="webhooks-page">
      <div className="flex items-center justify-between">
        <div>
          <PageHeader title="Webhooks" subtitle="Send conversation events to your own URLs in real time." />
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-webhook-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Webhook
            </Button>
          </DialogTrigger>
          <DialogContent className="rounded-none">
            <DialogHeader>
              <DialogTitle>Create Webhook</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div className="space-y-2">
                <Label className="overline">Endpoint URL</Label>
                <Input
                  value={newWebhook.url}
                  onChange={(e) => setNewWebhook({ ...newWebhook, url: e.target.value })}
                  placeholder="https://your-server.com/webhook"
                  className="rounded-none"
                  data-testid="webhook-url-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="overline">Events</Label>
                <div className="space-y-3">
                  {WEBHOOK_EVENTS.map(event => (
                    <div key={event.id} className="flex items-start gap-3">
                      <Checkbox
                        id={event.id}
                        checked={newWebhook.events.includes(event.id)}
                        onCheckedChange={() => toggleEvent(event.id)}
                        data-testid={`event-${event.id}`}
                      />
                      <div>
                        <label htmlFor={event.id} className="text-sm font-medium cursor-pointer">
                          {event.label}
                        </label>
                        <p className="text-xs text-muted-foreground">{event.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <div className="flex items-start gap-3">
                  <Switch
                    id="redact-pii"
                    checked={newWebhook.redact_pii !== false}
                    onCheckedChange={(checked) => setNewWebhook({ ...newWebhook, redact_pii: checked })}
                    data-testid="redact-pii-switch"
                  />
                  <div>
                    <label htmlFor="redact-pii" className="text-sm font-medium cursor-pointer">
                      Remove personal data from chat messages
                    </label>
                    <p className="text-xs text-muted-foreground">
                      IC, phone, email, card and bank numbers in visitor messages and replies are replaced before sending. Lead and appointment data are sent as is.
                    </p>
                  </div>
                </div>
              </div>
              <Button onClick={handleCreate} className="w-full" disabled={creating} data-testid="submit-webhook-btn">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Create Webhook
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {webhooks.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border">
          <Webhook className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
          <h3 className="font-semibold mb-2">No webhooks configured</h3>
          <p className="text-muted-foreground mb-4">Create webhooks to receive real-time event notifications</p>
          <Button onClick={() => setCreateOpen(true)} data-testid="empty-create-webhook">
            <Plus className="w-4 h-4 mr-2" /> Add Webhook
          </Button>
        </div>
      ) : (
        <div className="border border-border divide-y divide-border">
          {webhooks.map(webhook => (
            <div key={webhook.id} className="p-6" data-testid={`webhook-${webhook.id}`}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <code className="text-sm bg-muted px-2 py-1 break-all">{webhook.url}</code>
                    {!webhook.is_active && (
                      <span className="status-badge status-untrained">Disabled</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {webhook.events?.map(event => (
                      <span key={event} className="text-xs bg-muted px-2 py-1">
                        {WEBHOOK_EVENTS.find(e => e.id === event)?.label || event}
                      </span>
                    ))}
                  </div>
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => handleToggleRedact(webhook.id, webhook.redact_pii !== false)}
                      className="text-xs text-muted-foreground hover:text-foreground underline"
                      data-testid={`redact-pii-toggle-${webhook.id}`}
                    >
                      PII redaction: {webhook.redact_pii !== false ? "On" : "Off"} (click to change)
                    </button>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={webhook.is_active}
                    onCheckedChange={() => handleToggle(webhook.id, webhook.is_active)}
                    data-testid={`webhook-toggle-${webhook.id}`}
                  />
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDelete(webhook.id)}
                    className="h-8 w-8 text-muted-foreground hover:text-[hsl(var(--signal-red))]"
                    data-testid={`delete-webhook-${webhook.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Secret */}
              <div className="flex items-center gap-2 mt-4 pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground">Secret:</p>
                <code className="text-sm bg-muted px-2 py-1 flex-1">
                  {showSecret[webhook.id] ? secrets[webhook.id] : "••••••••••••••••••••••••"}
                </code>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => handleShowSecret(webhook.id)}
                  className="h-8 w-8"
                  data-testid={`show-secret-${webhook.id}`}
                >
                  {showSecret[webhook.id] ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                {showSecret[webhook.id] && secrets[webhook.id] && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copySecret(webhook.id)}
                    className="h-8 w-8"
                    data-testid={`copy-secret-${webhook.id}`}
                  >
                    {copied[webhook.id] ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                )}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => handleRotateSecret(webhook.id)}
            className="h-8 w-8"
            title="Rotate signing secret"
            data-testid={`rotate-secret-${webhook.id}`}
          >
            <RotateCw className="w-4 h-4" />
          </Button>
              </div>

            {/* Deliveries panel */}
            <div className="mt-4 pt-4 border-t border-border">
              <button type="button" onClick={() => toggleDeliveries(webhook.id)} className="text-sm text-blue-600 hover:underline" data-testid={`toggle-deliveries-${webhook.id}`}>
                {showDeliveries[webhook.id] ? "Hide recent deliveries" : "View recent deliveries"}
              </button>
              {showDeliveries[webhook.id] && (
                <div className="mt-3 text-xs" data-testid={`deliveries-${webhook.id}`}>
                  {loadingDeliveries[webhook.id] ? (
                    <div className="text-muted-foreground">Loading deliveries...</div>
                  ) : (deliveries[webhook.id] && deliveries[webhook.id].length > 0) ? (
                    <div className="space-y-1">
                      {deliveries[webhook.id].map((d) => (
                        <div key={d.id} className="flex items-center justify-between gap-2 p-2 bg-muted/30 rounded-none">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <span className={`inline-block w-2 h-2 rounded-full ${d.ok ? "bg-emerald-500" : "bg-red-500"}`} />
                            <span className="font-mono text-[11px]">{d.status_code || "ERR"}</span>
                            <span className="text-muted-foreground truncate">{d.event}</span>
                            <span className="text-muted-foreground text-[10px] ml-auto whitespace-nowrap">{d.created_at ? new Date(d.created_at).toLocaleString() : ""}</span>
                          </div>
                          <button type="button" disabled={retrying[d.id]} onClick={() => handleRetry(d.id, webhook.id)} className="text-blue-600 hover:underline disabled:opacity-50 whitespace-nowrap" data-testid={`retry-delivery-${d.id}`}>
                            {retrying[d.id] ? "Retrying..." : "Retry"}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-muted-foreground">No deliveries recorded yet.</div>
                  )}
                </div>
              )}
            </div>
            </div>
          ))}
        </div>
      )}

      {/* Documentation */}
      <div className="border border-border p-6">
        <h3 className="font-semibold mb-4">Webhook Payload</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Webhooks are sent as POST requests with JSON payload. Each request includes a signature header for verification.
        </p>
        <pre className="bg-muted p-4 text-sm overflow-x-auto">
{`// Headers
X-Webhook-Signature: <hmac-sha256-signature>

// Payload
{
  "event": "new_message",
  "chatbot_id": "...",
  "session_id": "...",
  "data": { ... },
  "timestamp": "2026-01-15T10:30:00Z"
}`}
        </pre>
      </div>
    </div>
  );
}
